// Settings › Campaigns' conversions to Google Ads (BL-091): the ads.conversions switch's words, and the
// map of each kind to its Google Ads conversion action (ads.conversionActions) as the form holds it. The
// server refuses an action set for two kinds (Google would count those jobs twice); the form says so
// first, by the kinds' names, so nothing is sent to be refused.

export const KINDS = ['lead', 'quoted', 'booked', 'completed'] as const;
export type Kind = (typeof KINDS)[number];
export type Method = 'click' | 'call';
export type ActionMap = Record<Kind, { click: string | null; call: string | null }>;
/** The form: each kind's action id by click and by call, as typed. */
export type MapDraft = Record<Kind, { click: string; call: string }>;

/** Each kind as the office says it, and what makes one. */
export const KIND_WORDS: Record<Kind, [string, string]> = {
  lead: ['Lead', 'A call from a Google ad or number that was a lead, like “Qualified Lead”.'],
  quoted: ['Quoted', 'A job’s first estimate shown, like “Quoted Job”, with its total before tax.'],
  booked: ['Booked', 'A job booked from a Google ad.'],
  completed: ['Completed', 'A job completed and invoiced, like “Booked Job”, with its invoices before tax.'],
};
const METHOD_WORDS: Record<Method, string> = { click: 'by click', call: 'by call' };

export const draftOf = (m: ActionMap): MapDraft =>
  Object.fromEntries(KINDS.map((k) => [k, { click: m[k].click ?? '', call: m[k].call ?? '' }])) as MapDraft;

/**
 * The setting's value from the form, or what's wrong with it: an id is its digits, and each id counts
 * one kind by one method; two on one action would count every job twice.
 */
export function mapOf(d: MapDraft): { value: ActionMap } | { error: string } {
  const seen = new Map<string, string>();
  const value = {} as ActionMap;
  for (const k of KINDS) {
    value[k] = { click: null, call: null };
    for (const m of ['click', 'call'] as const) {
      const id = d[k][m].trim().replace(/-/g, '');
      if (!id) continue;
      const where = `${KIND_WORDS[k][0]} ${METHOD_WORDS[m]}`;
      if (!/^\d{1,20}$/.test(id))
        return { error: `${where}: a conversion action id is its digits, from Google Ads.` };
      const before = seen.get(id);
      if (before)
        return {
          error: `${before} and ${where} are both action ${id}: Google would count those jobs twice. Give each its own action.`,
        };
      seen.set(id, where);
      value[k][m] = id;
    }
  }
  return { value };
}

/** The switch in words: on from its first day, or off. */
export const switchWords = (on: boolean, from: string | null): string =>
  on ? `On, from ${from ?? 'today'}. Nothing before that day is sent.` : 'Off. Nothing is sent to Google.';

/** Where the map comes from, in words. */
export const mapFromWords = (from: 'setting' | 'account' | 'none'): string =>
  from === 'setting'
    ? 'Set here.'
    : from === 'account'
      ? 'As the Google Ads account was connected. Saving here replaces it.'
      : 'No action is set for any kind yet, so nothing would be sent.';
